// API client for Wordsheet Discord Activity
// Proxies Firestore operations through the Cloudflare Worker

import { getFirebaseIdToken } from './discord.js';

const API_BASE = '/.proxy/api';

/**
 * Get headers for API requests, including Authorization if available
 */
function getHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const token = getFirebaseIdToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
}

class GameAPI {
    constructor() {
        this.pollInterval = null;
        this.pollCallback = null;
        this.currentGameCode = null;
    }

    async createGame(nickname) {
        const response = await fetch(`${API_BASE}/games`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ nickname }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to create game');
        }

        const data = await response.json();
        return data;
    }

    async getGame(gameCode) {
        const response = await fetch(`${API_BASE}/games/${gameCode}`, {
            headers: getHeaders(),
        });

        if (!response.ok) {
            if (response.status === 404) {
                return null;
            }
            const error = await response.json();
            throw new Error(error.error || 'Failed to get game');
        }

        const data = await response.json();
        return data.game;
    }

    async joinGame(gameCode, nickname) {
        const response = await fetch(`${API_BASE}/games/${gameCode}/join`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ nickname }),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to join game');
        }

        return await response.json();
    }

    async leaveGame(gameCode) {
        const response = await fetch(`${API_BASE}/games/${gameCode}/leave`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({}),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to leave game');
        }

        return await response.json();
    }

    async startGame(gameCode, gameFlags = null) {
        const body = gameFlags ? { gameFlags } : {};
        const response = await fetch(`${API_BASE}/games/${gameCode}/start`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to start game');
        }

        return await response.json();
    }

    // Start polling for game updates
    startPolling(gameCode, callback, interval = 1000) {
        this.stopPolling();
        this.currentGameCode = gameCode;
        this.pollCallback = callback;

        const poll = async () => {
            try {
                const game = await this.getGame(gameCode);
                if (game && this.pollCallback) {
                    this.pollCallback(game);
                }
            } catch (error) {
                console.error('Polling error:', error);
            }
        };

        // Initial poll
        poll();

        // Set up interval
        this.pollInterval = setInterval(poll, interval);
    }

    // Stop polling
    stopPolling() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
        this.pollCallback = null;
        this.currentGameCode = null;
    }
}

export const gameAPI = new GameAPI();

/**
 * Get headers for direct fetch calls (used by discord-multiplayer.js)
 */
export { getHeaders };
